
import { tap, timeout, debounceTime, switchMap, finalize } from 'rxjs/operators';
import { StorageService } from 'app/services/storage.service';
import { VehicleSearchResultModel } from 'app/models/dms/vehicleSearchResult.model';
import { EntityType, VehicleOrderBy } from 'app/utility/enums';
import { Component, OnInit, OnDestroy, Inject, Input, Output, HostListener, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';

import { Observable, BehaviorSubject, Subscription } from 'rxjs';

import { VehicleSummaryModel } from 'app/models/dms/vehicle.model';
import { VehicleSearchParamsModel } from 'app/models/dms/vehicleSearchParams.model';
import { ContactModel, ContactResultModel } from 'app/models/dms/contact.model';
import { UserModel } from 'app/models/authentication/user.model';
import { ListItemModel } from 'app/models/shared/listItem.model';

import { VehicleService } from 'app/services/vehicle.service';
import { ContactService } from 'app/services/contact.service';
import { UserService } from 'app/services/user.service';
import { GeneralService } from 'app/services/general.service';
import { Constants } from 'app/utility/constants';
import { MemberVehicleOrderBy, LookupInternalCollection, SalesStatus } from 'app/utility/enums';

import { LookupInternalModel } from 'app/models/shared/lookup.model';
import { CacheService } from 'app/services/cache.service';

import { LoadingController, AlertController, NavController, Platform } from '@ionic/angular';
import { CustomFieldResultModel } from 'app/models/shared/customField.model';
import { StringHelper } from 'app/utility/stringhelper';
import { MessageService } from 'app/services/message.service';


const searchParamsCacheKey: string = 'search-params';


const VEHICLE_STATUS_ALL: string = 'All';

@Component({
    selector: 'app-vehicle-list',
    templateUrl: './vehicle-list.component.html',
    styleUrls: ['./vehicle-list.component.scss'],
    standalone: false 
})
export class VehicleListComponent implements OnInit, OnDestroy, OnChanges
{

    @Input() searchParams: VehicleSearchParamsModel;
    @Input() allowActions: boolean = true;
    @Input() addNew: boolean = false;
    @Input() modal: boolean = false;
    @Input() compact: boolean = false;
    @Input() loading: boolean = false;
    @Input() vehiclesIn: VehicleSummaryModel[] | null = null;
    @Input() excludeVehicleIds: string[] = [];
    @Input() excludeSoldInProgress: boolean = false;
    @Output() vehicleSelected = new EventEmitter<VehicleSummaryModel>();
    @Output() actionSelected = new EventEmitter<any>();
    @Output() newVehicleSelected = new EventEmitter<string>();
    private searchParamsSubject: BehaviorSubject<VehicleSearchParamsModel>;

    hasUserSearch: boolean = false;

    showSpinner: boolean = false;

    isLoadingNewSearch: boolean = false;
    searchReloading: boolean = false;
    searchLoading: boolean = false;

    private resetVehicles: boolean = true;
    vehicles: VehicleSummaryModel[];

    rowCnt: number;
    rowCntFormatted: string = "0";

    sortColumns: ListItemModel[];

    currentSortColumn: string;

    isMobile: boolean = false;

    selectedVehicle: VehicleSummaryModel;
    statusFilterActive: boolean = false;

    customFields: CustomFieldResultModel[] | undefined = undefined;
    showCustomFields: boolean = false;

    constructor(
        private vehicleService: VehicleService,
        private contactService: ContactService,
        private userService: UserService,
        private generalService: GeneralService,
        private cache: CacheService,
        private storage: StorageService,
        public loadingController: LoadingController,
        private alertController: AlertController,
        private navCtrl: NavController,
        private platform: Platform,
        private messageService: MessageService
    )
    {
        this.searchLoading = true;
    }

    @Input() inParams: VehicleSearchParamsModel;

    ngOnChanges(changes: SimpleChanges)
    {
        if (changes['loading'])
        {
            this.searchLoading = this.loading;
        }

        if (changes['vehiclesIn'])
        {
            this.applyVehiclesIn();
        }
    }

    ngOnInit()
    {
        this.platform.ready().then(() =>
        {
            this.isMobile = (this.platform.width() < 800);
            this.platform.resize.subscribe(() =>
            {
                this.isMobile = (this.platform.width() < 800);
            });
        });

        if (this.inParams == null)
        {
            this.inParams = new VehicleSearchParamsModel();
        }

        if (this.inParams.orderByIn == null)
        {
            this.inParams.orderByIn = VehicleOrderBy.StockNo;
            this.inParams.orderByDesc = true;
        }

        this.currentSortColumn = this.inParams.orderByIn;

        if (!this.inParams.enforce)
        {
            this.storage.get(Constants.IonicStorage_VehicleListSort).then((val) =>
            {
                if (val)
                {
                    this.currentSortColumn = val;
                    this.inParams.orderByIn = val;
                    this.inParams.orderByDesc = false;
                    if (val.includes("Desc"))
                    {
                        this.inParams.orderByIn = val.replace("Desc", "");
                        this.inParams.orderByDesc = true;
                    }
                }
            });
        }

        this.sortColumns = [
            new ListItemModel(VehicleOrderBy.StockNo + '', 'Stock no.', 'Low to High', 'asc'),
            new ListItemModel(VehicleOrderBy.StockNoDesc + '', 'Stock no.', 'High to Low', 'desc'),
            new ListItemModel(VehicleOrderBy.MakeModel + '', 'Make/Model', 'A to Z', 'asc'),
            new ListItemModel(VehicleOrderBy.MakeModelDesc + '', 'Make/Model', 'Z to A', 'desc'),
            new ListItemModel(VehicleOrderBy.Year + '', 'Year', 'Low to High', 'asc'),
            new ListItemModel(VehicleOrderBy.YearDesc + '', 'Year', 'High to Low', 'desc'),
            new ListItemModel(VehicleOrderBy.RetailPrice + '', 'Price', 'Low to High', 'asc'),
            new ListItemModel(VehicleOrderBy.RetailPriceDesc + '', 'Price', 'High to Low', 'desc'),
            new ListItemModel(VehicleOrderBy.CreatedDateDesc + '', 'Date added', 'New to Old', 'desc'),
            new ListItemModel(VehicleOrderBy.CreatedDate + '', 'Date added', 'Old to New', 'asc'),
            new ListItemModel(VehicleOrderBy.ModifiedDateDesc + '', 'Date modified', 'New to Old', 'desc'),
            new ListItemModel(VehicleOrderBy.ModifiedDate + '', 'Date modified', 'Old to New', 'asc'),
            new ListItemModel(VehicleOrderBy.OpportCountDesc + '', 'Opportunity count', 'High to Low', 'desc'),
            new ListItemModel(VehicleOrderBy.OpportCount + '', 'Opportunity count', 'Low to High', 'asc'),
        ];

        this.applyVehiclesIn();
        if (!this.vehiclesIn)
        {
            this.initSearch();
        }

        const vehicleMessageSub = this.messageService.vehicleMessage.subscribe(
            (message) =>
            {
                console.log("dms vehicle message received ", message);

                if (!this.vehicles || this.vehicles.length === 0)
                {
                    return;
                }

                for (let i = 0; i < this.vehicles.length; i++)
                {
                    if (this.vehicles[i].vehicleId == message.vehicleId)
                    {
                        this.vehicles[i] = message;
                    }
                }
            }
        );
        this.subscriptions.push(vehicleMessageSub);
    }

    private applyVehiclesIn(): void
    {
        if (!this.vehiclesIn)
        {
            return;
        }

        this.resetVehicles = false;
        this.showSpinner = false;
        this.isLoadingNewSearch = false;
        this.searchReloading = false;
        this.searchLoading = this.loading;

        this.vehicles = [...this.vehiclesIn];
        this.rowCnt = this.vehicles.length;
        this.rowCntFormatted = this.numberWithCommas(this.rowCnt);

        if (this.vehicles.length > 0)
        {
            this.vehicleService.populateVehicleThumbnailUrls(this.vehicles);
        }
    }

    subscriptions: Subscription[] = [];

    ngOnDestroy()
    {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    private initSearch(): void
    {
        this.resetVehicles = true;
        this.showSpinner = true;

        this.inParams.pageNumber = 1;
        this.inParams.orderByIn = this.inParams.orderByIn.replace("Desc", "");


        if (this.userService.mcMinVersion("3.2.20"))
        {
            this.customFields = [];
            this.generalService.getCachedCustomFields(EntityType.Vehicle)
                .pipe( 
                    finalize(() =>
                    {
                        this.loadData(); // Ensures this runs after the observable completes or errors 
                    })
                )
                .subscribe({
                    next: (x) =>
                    {
                        this.customFields = x.filter(y => y.showInGrid);
                    },
                    error: (err) =>
                    {
                        console.error('Error fetching custom fields:', err);
                    }
                });
        }
        else
        {
            this.loadData();
        }

    }

    searchAllStatuses()
    {
        this.inParams.vehicleSoldStateIn = VEHICLE_STATUS_ALL;
        this.loadData();
    }

    loadData(event = null)
    {
        this.statusFilterActive = (this.inParams.vehicleSoldStateIn != VEHICLE_STATUS_ALL);

        this.getVehicles(this.inParams)
            .pipe(timeout(Constants.ApiDefaultTimeout))
            .subscribe({
                next: x =>
                {
                    if (event)
                    {
                        event.target.complete();
                        event.target.disabled = !(this.hasMore());
                    }

                    // Filter out vehicles that exist in excludeVehicleIds
                    if (this.excludeVehicleIds && this.excludeVehicleIds.length > 0)
                    {
                        const before = x.vehicles.length;
                        x.vehicles = x.vehicles.filter(v => !this.excludeVehicleIds.includes(v.vehicleId));
                        const after = x.vehicles.length;
                        x.resultCount -= before - after;
                    }

                    if (this.excludeSoldInProgress)
                    {
                        const before = x.vehicles.length;
                        x.vehicles = x.vehicles.filter(v => v.vehicleStatusCaption !== Constants.Vehicle_Status_SoldInProgress_Caption);
                        const after = x.vehicles.length;
                        x.resultCount -= before - after;
                    }

                    this.showSpinner = false;
                    this.isLoadingNewSearch = false;
                    this.searchLoading = false;
                    this.searchReloading = false;
                    this.rowCnt = x.resultCount;
                    
                    if (x.resultCount > 0 || this.inParams.pageNumber == 1)
                    {
                        this.rowCntFormatted = this.numberWithCommas(x.resultCount);
                    }

                    if (x && x.vehicles && x.vehicles.length > 0)
                    {
                        this.vehicleService.populateVehicleThumbnailUrls(x.vehicles);
                    }

                    this.handleResults(x.vehicles);
                },
                error: err =>
                {
                    this.showSpinner = false;
                    this.isLoadingNewSearch = false;
                    this.searchLoading = false;
                    this.searchReloading = false;
                    console.error(err);
                    this.userService.handleError(err);
                }
            });
    }

    private setCachedParams(prms: VehicleSearchParamsModel): void
    {
        this.cache.set(searchParamsCacheKey, prms, 30 * 24 * 60 * 1000);
    }

    private resultWatcher: Observable<VehicleSearchResultModel>;

    async loadSpinner()
    {
        const loading = await this.loadingController.create({
            mode: "ios",
            message: 'Loading...',
            translucent: true,
            cssClass: 'custom-class custom-loading',
            duration: 60000
        });
        return await loading.present();
    }

    private handleResults(vehicles: VehicleSummaryModel[]): void
    {
        if (this.resetVehicles)
        {
            this.vehicles = [];
            this.resetVehicles = false;
        }
        this.vehicles.push.apply(this.vehicles, vehicles);
    }
    private getVehicles(prms: VehicleSearchParamsModel): Observable<VehicleSearchResultModel>
    {
        return this.vehicleService.getVehicles(prms);
    }

    public hasMore(): boolean
    {
        if (this.vehiclesIn)
        {
            return false;
        }
        return this.vehicles && this.vehicles.length < this.rowCnt;
    }

    public loadMore(): void
    {
        this.searchLoading = true;
        this.inParams.pageNumber++;
        this.loadData();
    }

    sortChanged(ev: any)
    {
        this.isLoadingNewSearch = true;
        this.searchReloading = true;

        this.inParams.orderByIn = ev.replace("Desc", "");

        if (!this.inParams.enforce)
        {
            this.storage.set(Constants.IonicStorage_VehicleListSort, ev);
        }

        if (ev.includes("Desc"))
        {
            this.inParams.orderByDesc = true;
        }
        else
        {
            this.inParams.orderByDesc = false;
        }
        this.initSearch();
    }

    public selectItem(vehicle: VehicleSummaryModel)
    {
        // console.log("selectItem", vehicle);
        this.selectedVehicle = vehicle;
        this.vehicleSelected.emit(vehicle);
    }

    public actionSelect(action: any)
    {
        if (action.action == 'deleted')
        {
            this.vehicles = this.vehicles.filter(v => v.vehicleId != action.vehicle.vehicleId);
        }
        else
        {
            this.actionSelected.emit(action);
        }
    }


    public handleError(error: any): void
    {
        this.userService.handleError(error);
    }


    async presentAlert(title: string, message: string, backText: string = "", okText: string = "OK", next: boolean = false, nextText: string = "Continue")
    {
        var buttons = [];
        if (backText)
        {
            buttons.push({
                text: backText,
                role: 'confirmback',
                cssClass: 'secondary',
                handler: () =>
                {
                    this.navCtrl.navigateBack("/vehicles");
                }
            });
        }

        buttons.push({
            text: okText,
            role: 'cancel',
            cssClass: 'secondary',
            handler: () =>
            {
                this.searchParamsSubject.next(this.inParams);
            }
        });

        // if (next)
        // {
        // 	buttons.push({
        // 		text: nextText,
        // 		role: 'test',
        // 		cssClass: 'secondary',
        // 		handler: () =>
        // 		{
        // 		}
        // 	});
        // }

        const alert = await this.alertController.create({
            header: title,
            message: message,
            buttons: buttons
        });

        await alert.present();
    }

    public numberWithCommas(x: any): string
    {
        return StringHelper.numberWithCommas(x);
    }
}
